import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { configurationLayers, configurationStatus, howItWorksFunctions, publicModules } from '../data/howItWorks';
import { fadeUp } from '../utils/motion';
export function HowItWorksPage() {
  return <>
      <PageHero eyebrow="How It Works" title="Connect knowledge. Coordinate work. Develop capability." lead="Noetivra brings the tools your organization needs into dedicated workspaces. Choose a solution, configure the relevant modules and connect the knowledge and services that support your work." data-id="element-2907" data-mp="2838khl2ms7s2">
        <a href="/solutions" className="mt-8 inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-2908" data-mp="1pc8t6d7nylvm">
          Find your solution <ArrowRight size={18} aria-hidden="true" data-id="element-2909" data-mp="30t8jx4ed4snj" />
        </a>
      </PageHero>

      <section aria-label="Three connected functions" className="bg-white border-b border-line" data-id="element-2910" data-mp="1svmovxpr4333">
        <ol className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2911" data-mp="1xto6sfbfxwcz">
          {howItWorksFunctions.map((item, index) => <motion.li key={item.id} id={item.id} {...fadeUp} className="scroll-mt-24 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16 border-t border-line first:border-t-0 py-14 lg:py-16" data-id="element-2912" data-mp="22s7tihxuzc6e" data-mpi={index}>
              <div className="lg:col-span-5" data-id="element-2913" data-mp="1f5syvc2ifcrs">
                <h2 className="flex items-start gap-4 font-heading font-bold text-3xl lg:text-4xl text-ink-dark tracking-tight leading-tight" data-id="element-2914" data-mp="1f2dj0710hzpb">
                  <span aria-hidden="true" className="mt-1 lg:mt-1.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-teal/60 text-sm font-semibold tracking-normal text-teal" data-id="element-2915" data-mp="1nxbijwyo0apm">
                    {index + 1}
                  </span>
                  {item.title}
                </h2>
              </div>
              <div className="lg:col-span-7 lg:pt-2" data-id="element-2916" data-mp="1ttd0zodm66n5">
                <p className="text-xl text-ink-dark leading-relaxed" data-id="element-2917" data-mp="1tqqu7qyjrv5z">{item.body}</p>
                <p className="mt-5 text-ink-muted leading-relaxed" data-id="element-2918" data-mp="28goa7jmbpek3">
                  <span className="font-semibold text-ink-dark" data-id="element-2919" data-mp="23jft7mcwvgkf">For example: </span>
                  {item.example}
                </p>
                <a href={item.link.href} className="mt-6 inline-flex items-center gap-2 font-semibold text-navy hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-2920" data-mp="1fuwlb7xrx5sv">
                  {item.link.label} <ArrowRight size={16} aria-hidden="true" data-id="element-2921" data-mp="1kqtzp8ph8230" />
                </a>
              </div>
            </motion.li>)}
        </ol>
      </section>

      <section id="modules" aria-labelledby="configured-title" className="scroll-mt-20 bg-surface-bg border-b border-line py-20 lg:py-24" data-id="element-2922" data-mp="2w725rk449vjf">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2923" data-mp="33pjhlwzgtmfk">
          <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16 mb-12" data-id="element-2924" data-mp="38pec0aig8u5d">
            <h2 id="configured-title" className="lg:col-span-5 font-heading font-bold text-3xl lg:text-4xl text-ink-dark tracking-tight leading-tight" data-id="element-2925" data-mp="2tsn93r9kh0b0">
              Configured around your needs
            </h2>
            <p className="lg:col-span-7 lg:pt-2 text-lg text-ink-dark leading-relaxed" data-id="element-2926" data-mp="3mgsu8f4j8rbd">
              Every solution is built from the same four layers, so a research team and a public agency share one
              foundation while working in very different ways.
            </p>
          </motion.div>

          <motion.dl {...fadeUp} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line rounded-lg overflow-hidden" data-id="element-2927" data-mp="3hpkj9enk0xw3">
            {configurationLayers.map(layer => <div key={layer.name} className="bg-white p-6" data-id="element-2928" data-mp="e2as0u19w5zo">
                <dt className="font-heading font-bold text-lg text-ink-dark" data-id="element-2929" data-mp="2mibvisvn0hfo">
                  {layer.name === 'Core' || layer.name === 'Workspace' ? `Noetivra ${layer.name}` : layer.name}
                </dt>
                <dd className="mt-2 text-ink-muted leading-relaxed" data-id="element-2930" data-mp="2cjw6cvg67i8t">{layer.body}</dd>
              </div>)}
          </motion.dl>

          <motion.div {...fadeUp} className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16" data-id="element-2931" data-mp="27mm4uhfibg4m">
            <h3 className="lg:col-span-5 font-heading font-bold text-xl text-ink-dark" data-id="element-2932" data-mp="358a3l8zfqajb">Shared modules</h3>
            <div className="lg:col-span-7" data-id="element-2933" data-mp="30aiawcqyuke2">
              <table className="w-full text-left" data-id="element-2934" data-mp="2viqjjzzzkamv">
                <caption className="sr-only" data-id="element-2935" data-mp="2vh71c3urrpig">Public module examples and their purpose</caption>
                <thead data-id="element-2936" data-mp="2qjdud8ln6rh4">
                  <tr className="border-b border-ink-dark/70" data-id="element-2937" data-mp="2qi2sjebrbtsn">
                    <th scope="col" className="w-1/3 py-3 pr-4 text-sm font-semibold text-ink-dark" data-id="element-2938" data-mp="3o02v0yyuvnox">Module</th>
                    <th scope="col" className="py-3 text-sm font-semibold text-ink-dark" data-id="element-2939" data-mp="3j71aaabywtxf">Purpose</th>
                  </tr>
                </thead>
                <tbody data-id="element-2940" data-mp="3vik639mdrak0">
                  {publicModules.map(module => <tr key={module.name} className="border-b border-line align-top" data-id="element-2941" data-mp="3vkn7epszgq41">
                      <th scope="row" className="py-4 pr-4 font-heading font-semibold text-ink-dark" data-id="element-2942" data-mp="4elsu79t7edn">{module.name}</th>
                      <td className="py-4 text-ink-muted leading-relaxed" data-id="element-2943" data-mp="eaouzd4nvd0l">{module.purpose}</td>
                    </tr>)}
                </tbody>
              </table>
              <p className="mt-6 border-l-2 border-teal pl-4 text-sm text-ink-muted leading-relaxed" data-id="element-2944" data-mp="ia7d6153l9zq">{configurationStatus}</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-24" data-id="element-2945" data-mp="s47qgqaj9ljj">
        <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16" data-id="element-2946" data-mp="x1zkbpuqnhqd">
          <h2 className="lg:col-span-5 font-heading font-bold text-3xl text-ink-dark tracking-tight leading-tight" data-id="element-2947" data-mp="120j5vp5f6ri7">Go deeper</h2>
          <ul className="lg:col-span-7 border-t border-line" data-id="element-2948" data-mp="7d2ik84w7fyz">
            {[{
            label: 'Semantic Knowledge Platform',
            href: '/semantic-knowledge'
          }, {
            label: 'Noetivra',
            href: '/noetivra'
          }, {
            label: 'Human Capability Framework, CJI and NCJI',
            href: '/human-capability'
          }, {
            label: 'LLM vs. SKP: what’s the difference?',
            href: '/llm-vs-skp'
          }, {
            label: 'Compare all demonstrations',
            href: '/noetivra/demo-center'
          }].map(link => <li key={link.href} className="border-b border-line" data-id="element-2949" data-mp="151cm4rkbubs7">
                <a href={link.href} className="group flex items-center justify-between gap-4 py-4 font-heading font-semibold text-ink-dark hover:text-navy transition-colors duration-150" data-id="element-2950" data-mp="14ygnt24khl38">
                  {link.label}
                  <ArrowRight size={16} aria-hidden="true" className="shrink-0 text-ink-muted group-hover:text-navy transition-colors duration-150" data-id="element-2951" data-mp="31unw9yfiavwy" />
                </a>
              </li>)}
          </ul>
        </motion.div>
      </section>
    </>;
}